/**
 * A family's school year: the checklist to enroll for it, or where the year
 * stands. The year is the URL's, not whichever the app last had selected, so a
 * link to one year never shows another.
 *
 * A year enrolling on its checklist shows the steps and one way on. Every other
 * year (enrolled, declined, still on the old flow, or not open to the family)
 * reads as it did on the old enrollment page.
 */
import type { ResultOf } from 'gql.tada';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { AppLayoutContext } from '~/types';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { useViewAsFamily } from '~/hooks/useViewAsFamily';
import { useMyFamilyQuery, type MyFamilyQuery } from '../MyFamily/useMyFamilyQuery';
import { parentStudentStatus } from '../ParentEnrollment/parentStudentStatus';
import { formatChecklistDate, itemStateLabel } from '../FamilyDetail/checklistWords';
import { useEnrollYear } from './enrollYear';
import { gradeLabel, NO_ENROLLMENT, Notice } from './shared';

type EnrollmentForYear = NonNullable<NonNullable<ResultOf<typeof MyFamilyQuery>['myFamily']>['familyEnrollmentForYear']>;
type Checklist = NonNullable<EnrollmentForYear['checklist']>;
type ChecklistItem = Checklist['items'][number];

// The old flow's token link, for a family still part-way through it (deleted in P7).
const OLD_FLOW_OPEN_STATES = ['INVITED', 'IN_PROGRESS'];

// A step the family can still do something with.
const UNFINISHED_STATES = ['OPEN', 'IN_PROGRESS'];

export default function EnrollChecklist() {
  const { school } = useOutletContext<AppLayoutContext>();
  const { yearSlug } = useParams();
  const enrollYear = useEnrollYear();
  const year = enrollYear.status === 'ready' ? enrollYear.year : null;
  const { familyId: viewAsFamilyId, readOnly, basePath } = useViewAsFamily(school?.slug);
  // Fresh each time it opens: a step saved or submitted since changes what it shows.
  const { data, loading, error } = useMyFamilyQuery(
    year ? school?.id : undefined,
    year?.id,
    viewAsFamilyId ?? undefined,
    { fetchPolicy: 'cache-and-network' }
  );

  if (enrollYear.status === 'loading' || (year && loading && !data)) {
    return <div className="p-4 text-muted-foreground md:p-6">Loading…</div>;
  }
  if (year && error && !data) {
    return <div className="p-4 text-destructive md:p-6">Something went wrong loading your enrollment.</div>;
  }

  const family = year ? data?.myFamily : null;
  const enrollment = family?.familyEnrollmentForYear;
  const stepPath = (itemId: string) => `${basePath}/years/${yearSlug}/enroll/${itemId}`;

  if (year && enrollment?.access === 'ENROLLING' && enrollment.checklist) {
    return (
      <ChecklistOverview
        enrollment={enrollment}
        checklist={enrollment.checklist}
        yearName={year.name}
        familyName={family?.name}
        stepPath={stepPath}
        readOnly={readOnly}
      />
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold md:text-3xl">Enrollment</h1>
        {year ? (
          <p className="text-muted-foreground">
            {family?.name ? `${family.name} · ${year.name}` : year.name}
          </p>
        ) : null}
      </div>

      <YearEnrollment enrollment={enrollment} yearName={year?.name ?? 'this school year'} readOnly={readOnly} stepPath={stepPath} />
    </div>
  );
}

function ChecklistOverview({ enrollment, checklist, yearName, familyName, stepPath, readOnly }: {
  enrollment: EnrollmentForYear;
  checklist: Checklist;
  yearName: string;
  familyName?: string | null;
  stepPath: (itemId: string) => string;
  readOnly: boolean;
}) {
  const next = checklist.items.find((item) => UNFINISHED_STATES.includes(item.state));

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-4 md:p-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold md:text-3xl">Enroll for {yearName}</h1>
        {familyName ? <p className="text-muted-foreground">{familyName}</p> : null}
        <p className="text-sm text-muted-foreground">{checklist.progress.done} of {checklist.progress.total} done</p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4 md:p-6">
          {groupItems(checklist.items).map((group, index) => (
            <section key={group.key} className="space-y-2">
              <h2 id={`group-${index}`} className="text-base font-semibold">{group.title}</h2>
              <ul aria-labelledby={`group-${index}`} className="divide-y rounded-md border">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <ItemRow item={item} stepPath={stepPath} />
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {readOnly ? null : next ? (
            <Button asChild size="lg" className="w-full md:w-auto">
              <Link to={stepPath(next.id)}>Continue</Link>
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">Nothing needs you right now. Any next steps will show here.</p>
          )}
        </CardContent>
      </Card>

      {enrollment.studentEnrollments.length > 0 ? (
        <Card>
          <CardHeader className="p-4 md:p-6"><CardTitle className="text-lg">Your children</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
            <Children enrollment={enrollment} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/** The family's own steps first, then each child's, in the order the checklist lists them. */
function groupItems(items: readonly ChecklistItem[]) {
  const groups: { key: string; title: string; items: ChecklistItem[] }[] = [];
  const find = (key: string, title: string) => {
    let group = groups.find((entry) => entry.key === key);
    if (!group) {
      group = { key, title, items: [] };
      groups.push(group);
    }
    return group;
  };
  find('family', 'For your family');
  for (const item of items) {
    const group = item.personName ? find(item.personName, item.personName.split(' ')[0]) : find('family', 'For your family');
    group.items.push(item);
  }
  return groups.filter((group) => group.items.length > 0);
}

/**
 * How a row stands: its state, Kept as it is for an updated agreement the school kept as the office reads it, or,
 * while the fee's payment is on its way, the words the fee step uses.
 */
function rowLabel(item: ChecklistItem): string {
  return item.feeStatus === 'PAYMENT_PROCESSING' ? 'Payment processing' : itemStateLabel(item.state, item.keptAsItIs);
}

/** A step to open, or, while it waits for another, greyed out with why. */
function ItemRow({ item, stepPath }: { item: ChecklistItem; stepPath: (itemId: string) => string }) {
  if (item.state === 'LOCKED') {
    return (
      <div className="min-h-12 px-3 py-3 text-muted-foreground">
        <span className="block break-words">{item.title}</span>
        {item.lockedReason ? <span className="block text-sm">{item.lockedReason}</span> : null}
      </div>
    );
  }

  return (
    <Link to={stepPath(item.id)} className="flex min-h-12 items-center justify-between gap-3 px-3 py-3 hover:bg-muted/50">
      <span className="min-w-0 break-words">{item.title}</span>
      {/* A long label wraps on a narrow phone rather than breaking the title mid-word. */}
      <span className="flex min-w-0 items-center gap-1 text-right text-sm text-muted-foreground">
        {rowLabel(item)}
        <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />
      </span>
    </Link>
  );
}

function YearEnrollment({ enrollment, yearName, readOnly, stepPath }: {
  enrollment?: EnrollmentForYear | null;
  yearName: string;
  readOnly?: boolean;
  stepPath: (itemId: string) => string;
}) {
  if (!enrollment || enrollment.access === 'NOT_INVITED') {
    return <Notice title="Enrollment">{NO_ENROLLMENT}</Notice>;
  }

  if (enrollment.access === 'DECLINED') {
    return (
      <Notice title={`Not enrolled for ${yearName}`}>
        If you have questions about {yearName}, contact the school office.
      </Notice>
    );
  }

  const enrolled = enrollment.access === 'ENROLLED';
  const completed = enrollment.checklist?.completedItems ?? [];
  const toDo = enrolled ? (enrollment.checklist?.items ?? []).filter((item) => UNFINISHED_STATES.includes(item.state)) : [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{enrolled ? `Enrolled for ${yearName}` : `Enrollment for ${yearName} is in progress`}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Children enrollment={enrollment} />
          {enrolled ? null : <OldFlowNextStep enrollment={enrollment} readOnly={readOnly} />}
        </CardContent>
      </Card>

      {toDo.length > 0 ? (
        <Card>
          <CardHeader><h2 className="text-lg font-semibold leading-none tracking-tight">Still to do</h2></CardHeader>
          <CardContent>
            <ul className="divide-y rounded-md border">
              {toDo.map((item) => (
                <li key={item.id}>
                  <Link to={stepPath(item.id)} className="flex min-h-12 items-center justify-between gap-3 px-3 py-3 hover:bg-muted/50">
                    <span className="min-w-0 break-words">
                      {item.personName ? `${item.title} — ${item.personName.split(' ')[0]}` : item.title}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {enrolled && completed.length > 0 ? (
        <Card>
          <CardHeader><CardTitle>What you completed</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y text-sm">
              {completed.map((item) => (
                <li key={item.id} className="flex items-baseline justify-between gap-3 py-2">
                  <span>{item.personName ? `${item.title} — ${item.personName}` : item.title}</span>
                  {item.completedAt ? (
                    <span className="shrink-0 text-muted-foreground">{formatChecklistDate(item.completedAt)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}

function Children({ enrollment }: { enrollment: EnrollmentForYear }) {
  if (enrollment.studentEnrollments.length === 0) return null;

  return (
    <ul className="divide-y text-sm">
      {enrollment.studentEnrollments.map((se) => (
        <li key={se.id} className="flex items-baseline justify-between gap-3 py-2">
          <span className="min-w-0">
            <span>{se.person.firstName} {se.person.lastName}</span>
            {se.gradeLevel ? <span className="text-muted-foreground"> · {gradeLabel(se.gradeLevel)}</span> : null}
          </span>
          <span className="shrink-0 text-muted-foreground">{parentStudentStatus(se.state)}</span>
        </li>
      ))}
    </ul>
  );
}

function OldFlowNextStep({ enrollment, readOnly }: { enrollment: EnrollmentForYear; readOnly?: boolean }) {
  const canContinue = !readOnly && !!enrollment.inviteToken && OLD_FLOW_OPEN_STATES.includes(enrollment.state);
  if (!canContinue) {
    return <p className="text-sm text-muted-foreground">The school will email you the next step.</p>;
  }

  const path = enrollment.enrollmentKind === 'RE_ENROLLMENT' ? 'enroll' : 'apply';
  return (
    <Button asChild>
      <a href={`${window.location.origin}/app/${path}/${enrollment.inviteToken}`}>Continue your enrollment</a>
    </Button>
  );
}
